import { Component, computed, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
  OwlDateTimeModule,
  PickerType,
} from '@danielmoncada/angular-datetime-picker';
import {
  MomentDateTimeAdapter,
  OWL_MOMENT_DATE_TIME_ADAPTER_OPTIONS,
  OwlMomentDateTimeModule,
} from '@danielmoncada/angular-datetime-picker-moment-adapter';
import { FormlyModule } from '@ngx-formly/core';
import moment from 'moment';
import { map } from 'rxjs';

const MY_MOMENT_FORMATS = {
  parseInput: 'YYYY-MM-DD HH:mm',
  fullPickerInput: 'YYYY-MM-DD HH:mm',
  datePickerInput: 'YYYY-MM-DD',
  timePickerInput: 'HH:mm',
  monthYearLabel: 'MMM YYYY',
  dateA11yLabel: 'LL',
  monthYearA11yLabel: 'MMMM YYYY',
};

@Component({
  imports: [
    OwlMomentDateTimeModule,
    ReactiveFormsModule,
    OwlDateTimeModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    FormlyModule,
    MatSuffix,
  ],
  selector: 'vectronic-formly-datetime-picker',
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: DatetimePickerComponent },
    { provide: NG_VALIDATORS, multi: true, useExisting: DatetimePickerComponent },
    { provide: OWL_DATE_TIME_FORMATS, useValue: MY_MOMENT_FORMATS },
    { provide: DateTimeAdapter, useClass: MomentDateTimeAdapter },
    { provide: OWL_MOMENT_DATE_TIME_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
  template: ` <mat-form-field [owlDateTimeTrigger]="picker">
    <mat-label>{{ label() }}</mat-label>
    <input
      matInput
      readonly="true"
      [id]="id()"
      [name]="name()"
      [formlyAttributes]="formlyAttributes()"
      [tabIndex]="tabIndex()"
      [owlDateTime]="picker"
      [formControl]="dateControl"
      [min]="min()"
      [max]="max()"
      [required]="required()"
      [errorStateMatcher]="matcher"
    />
    @if (dateControl.hasError('owlDateTimeMin')) {
      <mat-error>Selected date cannot be earlier than {{ minReadable() }}</mat-error>
    } @else if (dateControl.hasError('owlDateTimeMax')) {
      <mat-error>Selected date cannot be later than {{ maxReadable() }}</mat-error>
    } @else if (invalid()) {
      <mat-error><ng-content></ng-content></mat-error>
    }
    <mat-icon matSuffix>today</mat-icon>
    <owl-date-time
      #picker
      [pickerType]="pickerType()"
      [startAt]="dateControl.getRawValue() ?? defaultDate()"
    ></owl-date-time>
  </mat-form-field>`,
  styles: `
    mat-form-field {
      width: 100%;
    }
    .owl-dt-trigger-disabled {
      opacity: unset;
      mat-icon {
        opacity: 0.35;
      }
    }
    mat-icon {
      color: #44474e;
    }
  `,
})
export class DatetimePickerComponent implements ControlValueAccessor, Validator {
  dateControl = new FormControl<moment.Moment>(undefined);

  // `invalid` lets a parent (e.g. a formly wrapper) signal that this control should be
  // shown as errored due to state - such as `required` - that `dateControl` itself never carries.
  matcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean => {
      const ownErrorState = !!(control?.touched || control?.value) && !!control?.invalid;
      return ownErrorState || this.invalid();
    },
  };

  min = input<string>();
  max = input<string>();
  name = input<string>();
  required = input<boolean>();
  id = input<string>();
  formlyAttributes = input();
  tabIndex = input<number>();
  label = input<string>('Choose a date');
  pickerType = input<PickerType>('both');
  defaultTime = input<string>();
  invalid = input<boolean>(false);
  defaultHours = computed(() =>
    !this.defaultTime() ? undefined : parseInt(this.defaultTime().split(':')[0]),
  );
  defaultMinutes = computed(() =>
    !this.defaultTime() ? undefined : parseInt(this.defaultTime().split(':')[1]),
  );
  defaultDate = computed(() => {
    if (!this.defaultTime()) {
      return undefined;
    }
    return (this.min() ? moment(this.min()).utc() : moment().utc().add(1, 'day')).set({
      hour: this.defaultHours(),
      minute: this.defaultMinutes(),
      second: 0,
      millisecond: 0,
    });
  });
  minReadable = computed(() =>
    this.min() ? moment(this.min()).utc().format('YYYY-MM-DD') : undefined,
  );
  maxReadable = computed(() =>
    this.max() ? moment(this.max()).utc().format('YYYY-MM-DD') : undefined,
  );

  onChange: (value: string) => void;
  onTouched: () => void;
  onValidatorChange: () => void;

  constructor() {
    this.dateControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        map((date) =>
          this.pickerType() === 'calendar' && Boolean(date)
            ? date.hour(this.defaultHours()).minute(this.defaultMinutes())
            : date,
        ),
        map((date) => (date ? date.second(0).toISOString() : undefined)),
      )
      .subscribe((value) => {
        this.onChange?.(value);
        this.onTouched?.();
      });

    this.dateControl.statusChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.onValidatorChange?.());
  }

  writeValue(value: string): void {
    const valueToSet = value != null ? moment.utc(value) : null;
    this.dateControl.setValue(valueToSet);
  }
  registerOnChange(fn: (value?: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.dateControl.errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }
}
